import * as THREE from 'three';

export function createRenderer(canvas: HTMLCanvasElement): THREE.WebGLRenderer {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: false,
    powerPreference: 'high-performance',
    // Needed for the video-recording pipeline — drawVideoFrame reads the
    // game canvas via ctx.drawImage() after composer.render(), and without
    // this the browser is free to clear the WebGL back buffer between the
    // render and the drawImage, leaving a blank game area in the recording.
    preserveDrawingBuffer: true,
  });
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.shadowMap.enabled = false;
  return renderer;
}

/**
 * Ortho camera must always contain the field + margin, regardless of the
 * container aspect ratio. If the container is wider than the field's aspect,
 * we fit vertically (extra background on the sides). If narrower, we fit
 * horizontally (extra background above/below). The boundary box on top +
 * sides always ends up on screen.
 */
/** Defaults match the desktop field (20 cols × 17 rows × 1.4 GRID_UNIT).
 *  Game passes its actual dims so mobile (14×15) fits without letterboxing. */
const DEFAULT_FIELD_HALFWIDTH = 14;
const DEFAULT_FIELD_HEIGHT = 23.8;
const RAIL_MARGIN = 0; // grid fills the canvas — no extra breathing room past the field

export function resizeRenderer(
  renderer: THREE.WebGLRenderer,
  camera: THREE.OrthographicCamera,
  maxDpr = 2,
  fieldHalfWidth: number = DEFAULT_FIELD_HALFWIDTH,
  fieldHeight: number = DEFAULT_FIELD_HEIGHT,
): boolean {
  const canvas = renderer.domElement;
  const width = Math.max(1, Math.floor(canvas.clientWidth));
  const height = Math.max(1, Math.floor(canvas.clientHeight));
  const dpr = Math.min(window.devicePixelRatio || 1, maxDpr);
  const bufferWidth = Math.floor(width * dpr);
  const bufferHeight = Math.floor(height * dpr);
  const needsResize = canvas.width !== bufferWidth || canvas.height !== bufferHeight;

  if (needsResize) {
    renderer.setPixelRatio(dpr);
    renderer.setSize(width, height, false);
  }

  const aspect = width / height;
  const desiredHalfHeight = fieldHeight / 2 + RAIL_MARGIN;
  const desiredHalfWidth = fieldHalfWidth + RAIL_MARGIN;
  const desiredAspect = desiredHalfWidth / desiredHalfHeight;

  let halfHeight: number;
  let halfWidth: number;
  if (aspect >= desiredAspect) {
    halfHeight = desiredHalfHeight;
    halfWidth = halfHeight * aspect;
  } else {
    halfWidth = desiredHalfWidth;
    halfHeight = halfWidth / aspect;
  }
  camera.left = -halfWidth;
  camera.right = halfWidth;
  camera.top = halfHeight;
  camera.bottom = -halfHeight;
  camera.updateProjectionMatrix();

  return needsResize;
}
